import { useEffect, useMemo, useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Languages,
  LockKeyhole,
  Menu,
  ShieldCheck,
  Sparkles,
  User,
  UserRound,
  X
} from 'lucide-react'
import { PROVER_SITE } from './config/site'
import MobileActionBar from './MobileActionBar'
import './prover.css'

const copy = {
  es: {
    nav: { home: 'Inicio', services: 'Servicios', about: 'Nosotros', specialists: 'Especialistas', surgery: 'Cirugías', contact: 'Contacto' },
    hero: { eyebrow: 'PROVER · CLÍNICA OFTALMOLÓGICA', title: 'La visión que transforma vidas', lead: 'Tecnología, experiencia y humanidad al cuidado de tu mirada.', cta: 'Conocé más sobre nosotros' },
    traits: [
      { icon: Eye, title: 'Tecnología de última generación' },
      { icon: ShieldCheck, title: 'Seguridad y confianza' },
      { icon: User, title: 'Atención personalizada' },
      { icon: Sparkles, title: 'Excelencia en cada detalle' }
    ],
    specialists: { title: 'Nuestros especialistas', subtitle: 'Profesionales dedicados a cuidar tu visión', cta: 'Ver perfil' },
    book: { title: 'Agendá tu consulta', desc: 'Elegí el día y horario que mejor se adapte a vos.', cta: 'Agendar ahora' },
    angel: { title: 'Consultá con Ángel', desc: 'Nuestro asistente virtual está listo para ayudarte.', cta: 'Chatear con Ángel' },
    surgery: { eyebrow: 'CIRUGÍAS Y ESTUDIOS ESPECIALIZADOS', title: 'Cirugías con precisión y cuidado', lead: 'Equipamiento de alta precisión para diagnóstico, tratamiento y cirugía oftalmológica.', cta: 'Agendar evaluación' },
    gallery: [
      { src: '/tenants/prover/cirugia.png', alt: 'Cirugía oftalmológica con microscopio quirúrgico' },
      { src: '/tenants/prover/consulta-dante.jpg', alt: 'Consulta oftalmológica pediátrica' },
      { src: '/tenants/prover/cirugia-galeria-1.jpg', alt: 'Cirugía oftalmológica en quirófano' },
      { src: '/tenants/prover/cirugia-galeria-2.jpg', alt: 'Procedimiento oftalmológico especializado' }
    ],
    newLocal: { eyebrow: 'NOS RENOVAMOS', title: 'Nuestra sede crece para verte mejor', lead: 'Estamos renovando las instalaciones de ProVer con espacios más modernos y tecnología al servicio de tu comodidad y salud visual.', cta: 'Ver ubicación' },
    location: { title: 'Dónde estamos', subtitle: 'Visitá nuestra clínica en Eligio Ayala, Asunción.', cta: 'Cómo llegar' },
    portal: 'Mi Portal',
    management: 'Panel de Gestión'
  },
  pt: {
    nav: { home: 'Início', services: 'Serviços', about: 'Sobre nós', specialists: 'Especialistas', surgery: 'Cirurgias', contact: 'Contato' },
    hero: { eyebrow: 'PROVER · CLÍNICA OFTALMOLÓGICA', title: 'A visão que transforma vidas', lead: 'Tecnologia, experiência e humanidade no cuidado do seu olhar.', cta: 'Conheça mais sobre nós' },
    traits: [
      { icon: Eye, title: 'Tecnologia de última geração' },
      { icon: ShieldCheck, title: 'Segurança e confiança' },
      { icon: User, title: 'Atendimento personalizado' },
      { icon: Sparkles, title: 'Excelência em cada detalhe' }
    ],
    specialists: { title: 'Nossos especialistas', subtitle: 'Profissionais dedicados a cuidar da sua visão', cta: 'Ver perfil' },
    book: { title: 'Agende sua consulta', desc: 'Escolha o dia e horário que melhor se adaptem a você.', cta: 'Agendar agora' },
    angel: { title: 'Fale com Ángel', desc: 'Nosso assistente virtual está pronto para ajudar.', cta: 'Chatear com Ángel' },
    surgery: { eyebrow: 'CIRURGIAS E EXAMES ESPECIALIZADOS', title: 'Cirurgias com precisão e cuidado', lead: 'Equipamento de alta precisão para diagnóstico, tratamento e cirurgia oftalmológica.', cta: 'Agendar avaliação' },
    gallery: [
      { src: '/tenants/prover/cirugia.png', alt: 'Cirurgia oftalmológica com microscópio cirúrgico' },
      { src: '/tenants/prover/consulta-dante.jpg', alt: 'Consulta oftalmológica pediátrica' },
      { src: '/tenants/prover/cirugia-galeria-1.jpg', alt: 'Cirurgia oftalmológica em centro cirúrgico' },
      { src: '/tenants/prover/cirugia-galeria-2.jpg', alt: 'Procedimento oftalmológico especializado' }
    ],
    newLocal: { eyebrow: 'ESTAMOS NOS RENOVANDO', title: 'Nossa sede cresce para cuidar melhor de você', lead: 'Estamos renovando as instalações da ProVer com espaços mais modernos e tecnologia a serviço do seu conforto e saúde visual.', cta: 'Ver localização' },
    location: { title: 'Onde estamos', subtitle: 'Visite nossa clínica em Eligio Ayala, Assunção.', cta: 'Como chegar' },
    portal: 'Meu Portal',
    management: 'Painel de Gestão'
  },
  en: {
    nav: { home: 'Home', services: 'Services', about: 'About', specialists: 'Specialists', surgery: 'Surgery', contact: 'Contact' },
    hero: { eyebrow: 'PROVER · OPHTHALMOLOGY CLINIC', title: 'The vision that transforms lives', lead: 'Technology, experience and humanity caring for your sight.', cta: 'Learn more about us' },
    traits: [
      { icon: Eye, title: 'State-of-the-art technology' },
      { icon: ShieldCheck, title: 'Safety and trust' },
      { icon: User, title: 'Personalized care' },
      { icon: Sparkles, title: 'Excellence in every detail' }
    ],
    specialists: { title: 'Our specialists', subtitle: 'Professionals dedicated to caring for your vision', cta: 'View profile' },
    book: { title: 'Book your appointment', desc: 'Choose the day and time that suits you best.', cta: 'Book now' },
    angel: { title: 'Ask Ángel', desc: 'Our virtual assistant is ready to help you.', cta: 'Chat with Ángel' },
    surgery: { eyebrow: 'SURGERY AND SPECIALIZED STUDIES', title: 'Surgery with precision and care', lead: 'High-precision equipment for diagnosis, treatment and ophthalmic surgery.', cta: 'Book an evaluation' },
    gallery: [
      { src: '/tenants/prover/cirugia.png', alt: 'Ophthalmic surgery with surgical microscope' },
      { src: '/tenants/prover/consulta-dante.jpg', alt: 'Pediatric ophthalmology consultation' },
      { src: '/tenants/prover/cirugia-galeria-1.jpg', alt: 'Ophthalmic surgery in operating room' },
      { src: '/tenants/prover/cirugia-galeria-2.jpg', alt: 'Specialized ophthalmic procedure' }
    ],
    newLocal: { eyebrow: 'WE ARE RENEWING', title: 'Our location is growing to see you better', lead: "We are renewing ProVer's facilities with more modern spaces and technology at the service of your comfort and eye health.", cta: 'See location' },
    location: { title: 'Where we are', subtitle: 'Visit our clinic at Eligio Ayala, Asunción.', cta: 'Get directions' },
    portal: 'My Portal',
    management: 'Management Panel'
  },
  gn: {
    nav: { home: 'Ñepyrũ', services: "Mba'epe", about: 'Ore rehegua', specialists: 'Pohãnohára', surgery: 'Cirugía', contact: 'Mombarete' },
    hero: { eyebrow: 'PROVER · CLÍNICA OFTALMOLÓGICA', title: "Tembi'apo porã rehecha", lead: 'Tembiporu, kuaapy ha ñangareko yvypóra rehegua.', cta: 'Eikuaave ore rehegua' },
    traits: [
      { icon: Eye, title: 'Tembiporu ipyahuvéva' },
      { icon: ShieldCheck, title: 'Tekorosã ha jerovia' },
      { icon: User, title: 'Ñangareko peteĩteĩva' },
      { icon: Sparkles, title: "Mba'eporã peteĩteĩva" }
    ],
    specialists: { title: 'Ore pohãnohára', subtitle: "Pohãnohára oñe'ẽ rehecha reko", cta: 'Hecha perfil' },
    book: { title: 'Emoĩ nde consulta', desc: 'Eiporavo ára ha aravo nde rehegua.', cta: 'Emoĩ agora' },
    angel: { title: "Eñe'ẽ Ángel ndive", desc: 'Ore asistente virtual oĩ añetehápe ayudu.', cta: "Eñe'ẽ Ángel ndive" },
    surgery: { eyebrow: 'CIRUGÍA HA ESTUDIO ESPECIALIZADO', title: "Cirugía presición ha ñangarekoha", lead: 'Tembiporu presisión rehegua diagóstiko, tratamiento ha cirugía oftalmológica rehegua.', cta: 'Emoĩ evaluación' },
    gallery: [
      { src: '/tenants/prover/cirugia.png', alt: 'Cirugía oftalmológica microscopio quirúrgico ndive' },
      { src: '/tenants/prover/consulta-dante.jpg', alt: 'Consulta oftalmológica pediátrica' },
      { src: '/tenants/prover/cirugia-galeria-1.jpg', alt: 'Cirugía oftalmológica quirófano-pe' },
      { src: '/tenants/prover/cirugia-galeria-2.jpg', alt: 'Procedimiento oftalmológico especializado' }
    ],
    newLocal: { eyebrow: 'ROÑEMOÑEPYÑU', title: 'Ore tenda oikove recha porã hag̃ua', lead: 'Roñemongeta ProVer róga, ndive tenda ipyahúva ha tembiporu oipytyvõvo nde ñangareko ha recha reko.', cta: 'Ehecha moõpa' },
    location: { title: 'Moõ pa reikohína', subtitle: 'Eju Prover Eligio Ayala, Asunción-pe.', cta: "Mba'eicha eju" }
  }
}

function initials(name = '') {
  return name.replace(/Dr[a.]?/gi, '').split(' ').filter(Boolean).slice(0, 2).map(x => x[0]).join('').toUpperCase()
}

function compactName(name = '') {
  if (!name) return ''
  return name
    .replace(/\bvda\.?\s+de\b/gi, 'de')
    .split(' ')
    .map((word, i, arr) => {
      if (i === 0 || i >= arr.length - 2) return word
      if (/^Concepci[óo]n$/i.test(word)) return 'C.'
      if (/^Daniel$/i.test(word)) return 'D.'
      return word
    })
    .join(' ')
}

function normalizeName(name = '') {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export default function ProverHome() {
  const [lang, setLang] = useState('es')
  const [doctors, setDoctors] = useState(PROVER_SITE.doctorsFallback)
  const [active, setActive] = useState(1)
  const [menuOpen, setMenuOpen] = useState(false)
  const t = copy[lang]

  useEffect(() => {
    document.title = 'Prover Clínica Oftalmológica'
    const meta = document.querySelector('meta[name="description"]')
    if (meta) meta.setAttribute('content', 'Prover Clínica Oftalmológica en Asunción, Paraguay. Excelencia médica y tecnología de vanguardia para el cuidado de tu visión.')
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    fetch('/api/providers?tenant_id=prover', { headers: { 'x-tenant-id': 'prover' }, signal: controller.signal })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(payload => {
        const rows = payload?.data || payload?.providers || payload
        if (Array.isArray(rows) && rows.length) {
          const enriched = rows.slice(0, 3).map((row) => {
            const rowNameNorm = normalizeName(row.name || row.full_name);
            const fallback = PROVER_SITE.doctorsFallback.find(d =>
              d.id === row.id ||
              normalizeName(d.name) === rowNameNorm ||
              rowNameNorm.includes(normalizeName(d.name)) ||
              normalizeName(d.name).includes(rowNameNorm)
            );
            return fallback
              ? { ...fallback, ...row, specialty: fallback.specialty, regProf: fallback.regProf, photo_url: row.photo_url || fallback.photo_url }
              : { ...row, photo_url: row.photo_url || fallback?.photo_url };
          });
          setDoctors(enriched)
        }
      })
      .catch(() => {})
    return () => controller.abort()
  }, [])

  const arranged = useMemo(() => doctors.map((doctor, index) => ({ ...doctor, slot: index - active })), [doctors, active])
  const rotate = direction => setActive(current => (current + direction + doctors.length) % doctors.length)
  const openChat = (e) => {
    e.preventDefault();
    if (typeof window !== 'undefined' && window.openTenantChat) {
      window.openTenantChat();
    } else if (PROVER_SITE.whatsapp) {
      window.open(`https://wa.me/${PROVER_SITE.whatsapp}?text=${encodeURIComponent('Hola Ángel, necesito información de Prover Clínica Oftalmológica.')}`, '_blank', 'noopener,noreferrer');
    }
  }
  const angelUrl = PROVER_SITE.whatsapp
    ? `https://wa.me/${PROVER_SITE.whatsapp}?text=${encodeURIComponent('Hola Ángel, necesito información de Prover Clínica Oftalmológica.')}`
    : '#contacto'

  return (
    <main className="prover-site">
      <div className="prover-orb prover-orb-a" /><div className="prover-orb prover-orb-b" />

      <header className="prover-nav glass-panel">
        <a href="#inicio" className="prover-brand"><img src={PROVER_SITE.assets.logo} alt="Prover Clínica Oftalmológica" /></a>
        <nav className="prover-nav-links">
          <a href="#inicio" onClick={() => setMenuOpen(false)}>{t.nav.home}</a>
          <a href="#servicios" onClick={() => setMenuOpen(false)}>{t.nav.services}</a>
          <a href="#especialistas" onClick={() => setMenuOpen(false)}>{t.nav.specialists}</a>
          <a href="#cirugia" onClick={() => setMenuOpen(false)}>{t.nav.surgery}</a>
          <a href="#contacto" onClick={() => setMenuOpen(false)}>{t.nav.contact}</a>
        </nav>
        <div className="prover-actions">
          <label className="language-control" aria-label="Idioma"><Languages size={16}/><select aria-label="Seleccionar idioma" value={lang} onChange={e => setLang(e.target.value)}>{PROVER_SITE.languages.map(code => <option key={code} value={code}>{code.toUpperCase()}</option>)}</select></label>
          <a className="nav-pill portal-pill" href={PROVER_SITE.routes.patientPortal}><UserRound size={16}/>{t.portal}</a>
          <a className="nav-pill secure" href={PROVER_SITE.routes.management}><LockKeyhole size={16}/>{t.management}</a>
          <button type="button" className="menu-toggle" aria-label="Abrir menú" aria-expanded={menuOpen} onClick={() => setMenuOpen(v => !v)}>
            {menuOpen ? <X size={24}/> : <Menu size={24}/>}
          </button>
        </div>
      </header>

      {menuOpen && (
        <div className="mobile-drawer" onClick={(e) => { if (e.target === e.currentTarget) setMenuOpen(false) }}>
          <nav className="mobile-drawer-nav glass-panel" aria-label="Menú móvil">
            <button type="button" className="mobile-drawer-close" aria-label="Cerrar menú" onClick={() => setMenuOpen(false)}><X size={24}/></button>
            <a href="#inicio" onClick={() => setMenuOpen(false)}>{t.nav.home}</a>
            <a href="#servicios" onClick={() => setMenuOpen(false)}>{t.nav.services}</a>
            <a href="#especialistas" onClick={() => setMenuOpen(false)}>{t.nav.specialists}</a>
            <a href="#cirugia" onClick={() => setMenuOpen(false)}>{t.nav.surgery}</a>
            <a href="#ubicacion" onClick={() => setMenuOpen(false)}>{t.nav.contact}</a>
            <a href={PROVER_SITE.routes.patientPortal} className="mobile-drawer-cta" onClick={() => setMenuOpen(false)}>{t.portal}</a>
            <a href={PROVER_SITE.routes.management} className="mobile-drawer-cta secure" onClick={() => setMenuOpen(false)}>{t.management}</a>
          </nav>
        </div>
      )}

      <section id="inicio" className="prover-hero">
        <div className="hero-copy">
          <span className="eyebrow">{t.hero.eyebrow}</span>
          <h1>{t.hero.title}</h1>
          <p>{t.hero.lead}</p>
          <div className="hero-buttons"><a className="primary-button" href="#servicios">{t.hero.cta} <span>→</span></a></div>

          <div className="hero-float-cards">
            <a className="float-card glass-panel" href={PROVER_SITE.routes.booking}>
              <div className="float-icon float-calendar-img">
                <img src="/tenants/prover/float-calendar.jpg" alt="Agendá tu consulta" />
              </div>
              <div className="float-body">
                <span className="float-title">{t.book.title}</span>
                <span className="float-desc">{t.book.desc}</span>
                <span className="float-cta">{t.book.cta} <span>→</span></span>
              </div>
            </a>
            <button type="button" className="float-card glass-panel angel" onClick={openChat}>
              <div className="float-icon float-angel-img">
                <img src="/tenants/prover/angel-bot.jpg" alt="Ángel asistente virtual" />
              </div>
              <div className="float-body">
                <span className="float-title">{t.angel.title}</span>
                <span className="float-desc">{t.angel.desc}</span>
                <span className="float-cta">{t.angel.cta} <span>→</span></span>
              </div>
            </button>
          </div>
        </div>
        <div className="hero-visual">
          <div className="hero-eye">
            <img src={PROVER_SITE.assets.isotipo} alt="Isotipo oficial de Prover" width="500" height="500" loading="eager" fetchpriority="high" decoding="async" />
          </div>
        </div>
      </section>

      <section className="prover-traits glass-panel">
        {t.traits.map(({ icon: Icon, title }) => (
          <div key={title} className="trait-item"><div className="trait-icon"><Icon size={22} strokeWidth={1.8}/></div><span>{title}</span></div>
        ))}
      </section>

      <section id="nuevo-local" className="new-local-section glass-panel">
        <div className="new-local-copy">
          <span className="eyebrow">{t.newLocal.eyebrow}</span>
          <h2>{t.newLocal.title}</h2>
          <p>{t.newLocal.lead}</p>
          <a className="primary-button" href="#ubicacion">{t.newLocal.cta} <span>→</span></a>
        </div>
        <div className="new-local-media">
          <img
            src="/tenants/prover/prover-local.jpg"
            alt="Fachada de ProVer Clínica Oftalmológica"
            width="1079"
            height="810"
            loading="lazy"
            decoding="async"
          />
        </div>
      </section>

      <section id="especialistas" className="doctor-section glass-panel">
        <div className="section-heading">
          <h2>{t.specialists.title}</h2>
          <p>{t.specialists.subtitle}</p>
        </div>
        <button className="carousel-arrow left" onClick={() => rotate(-1)} aria-label="Anterior"><ChevronLeft/></button>
        <div className="doctor-carousel">
          {arranged.map((doctor, index) => {
            const isActive = index === active
            return (
              <article
                key={doctor.id || doctor.name || index}
                className={`doctor-card ${isActive ? 'active' : ''}`}
                onClick={() => setActive(index)}
              >
                {isActive ? (
                  <div className="doctor-active-layout">
                    <div className="doctor-photo">{doctor.photo_url || doctor.photo ? <img src={doctor.photo_url || doctor.photo} alt={doctor.name || doctor.full_name}/> : <span>{initials(doctor.name || doctor.full_name)}</span>}</div>
                    <div className="doctor-meta">
                      <h3>{compactName(doctor.name || doctor.full_name)}</h3>
                      <p className="doctor-specialty">{doctor.specialty || doctor.specialty_name || 'Oftalmología'}</p>
                      {doctor.regProf && <span className="doctor-reg">{doctor.regProf}</span>}
                      <a className="doctor-cta" href={PROVER_SITE.routes.booking}>{t.specialists.cta} <span>→</span></a>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="doctor-photo">{doctor.photo_url || doctor.photo ? <img src={doctor.photo_url || doctor.photo} alt={doctor.name || doctor.full_name}/> : <span>{initials(doctor.name || doctor.full_name)}</span>}</div>
                    <div className="doctor-meta">
                      <h3>{compactName(doctor.name || doctor.full_name)}</h3>
                      <p className="doctor-specialty">{doctor.specialty || doctor.specialty_name || 'Oftalmología'}</p>
                      {doctor.regProf && <span className="doctor-reg">{doctor.regProf}</span>}
                    </div>
                  </>
                )}
              </article>
            )
          })}
        </div>
        <button className="carousel-arrow right" onClick={() => rotate(1)} aria-label="Siguiente"><ChevronRight/></button>
        <div className="carousel-dots">
          {doctors.map((doctor, index) => (
            <button
              key={doctor.id || index}
              className={`carousel-dot ${index === active ? 'active' : ''}`}
              onClick={() => setActive(index)}
              aria-label={`Ver ${doctor.name || doctor.full_name}`}
            />
          ))}
        </div>
      </section>

      <section id="cirugia" className="gallery-section glass-panel">
        <div className="gallery-copy">
          <div className="gallery-label"><Sparkles size={18}/><span>{t.surgery.eyebrow}</span></div>
          <h2>{t.surgery.title}</h2>
          <p>{t.surgery.lead}</p>
          <a className="primary-button" href={PROVER_SITE.routes.booking}>{t.surgery.cta} <span>→</span></a>
        </div>
        <div className="gallery-grid" aria-label="Galería de cirugías y estudios especializados">
          {t.gallery.map((img, i) => (
            <div className="gallery-item" key={i}>
              <img src={img.src} alt={img.alt} width="800" height="600" loading="lazy" decoding="async" />
            </div>
          ))}
        </div>
      </section>

      <section id="ubicacion" className="location-section glass-panel">
        <div className="location-copy">
          <h2>{t.location.title}</h2>
          <p>{t.location.subtitle}</p>
          <div className="location-address" style={{ whiteSpace: 'pre-line' }}>{PROVER_SITE.address}</div>
          <div className="location-actions">
            <a className="primary-button" href={`https://www.google.com/maps/dir/?api=1&destination=${PROVER_SITE.location.lat},${PROVER_SITE.location.lng}`} target="_blank" rel="noreferrer">Google Maps <span>→</span></a>
            {PROVER_SITE.location.directionsUrl && (
              <a className="primary-button waze" href={PROVER_SITE.location.directionsUrl} target="_blank" rel="noreferrer">Waze <span>→</span></a>
            )}
          </div>
        </div>
        <div className="location-map">
          <iframe
            title="Ubicación Prover Clínica Oftalmológica"
            src={PROVER_SITE.location.mapUrl}
            width="100%"
            height="100%"
            style={{ border: 0, borderRadius: 'inherit' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>

      <footer id="contacto" className="prover-footer glass-panel">
        <img src={PROVER_SITE.assets.logo} alt="Prover" className="footer-logo"/>
        <div className="footer-info">
          <a className="footer-location" href={PROVER_SITE.location.directionsUrl || `https://www.google.com/maps/dir/?api=1&destination=${PROVER_SITE.location.lat},${PROVER_SITE.location.lng}`} target="_blank" rel="noreferrer"><span className="footer-label" style={{ whiteSpace: 'pre-line' }}>{PROVER_SITE.address}</span></a>
          {PROVER_SITE.phone && (
            <a className="footer-phone" href={angelUrl} target="_blank" rel="noreferrer">{PROVER_SITE.phone} (WhatsApp / Ángel)</a>
          )}
        </div>
        <div className="footer-copy"><span>© 2026 ProVer Clínica Oftalmológica · Tecnología QorIAX.</span><span> · <a href="/privacidad">Privacidad</a> · <a href="/terminos">Términos</a></span></div>
      </footer>

      {/* Mobile bottom action bar: agenda, contacto y portal siempre al pie */}
      <MobileActionBar />
    </main>
  )
}
